import { inject, Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { BudgetDto, CalculateMarginRequestDto, CalculateMarginResponseDto } from "@dtos/transports/budgets.dto";
import { ApiService } from "@services/api.service";
import { EndPoints } from "@services/EndPoints";

// INTERFAZ PARA RESPUESTA PAGINADA
export interface PaginatedBudgetsResponse {
    count: number;
    total_pages: number;
    next: string | null;
    previous: string | null;
    results: BudgetDto[];
}

// INTERFAZ PARA RESULTADO DE OPERACIONES CON POSIBLE ERROR
export interface BudgetOperationResult<T> {
    success: boolean;
    data?: T;
    errorMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class BudgetsProvider {
    private readonly api = inject(ApiService);

    /**
     * OBTENER PRESUPUESTOS CON SOPORTE PARA FILTRADO, BÚSQUEDA, ORDENAMIENTO Y PAGINACIÓN
     * @param queryParams - OBJETO CON TODOS LOS PARÁMETROS DE CONSULTA
     * @returns RESPUESTA PAGINADA CON PRESUPUESTOS Y TOTAL
     */
    async getBudgets(queryParams: Record<string, any> = {}): Promise<{ list: BudgetDto[], totalRecords: number } | null> {
        try {
            // ESTABLECER VALORES POR DEFECTO SI NO SE PROPORCIONAN
            const params = {
                page: 1,
                page_size: 50,
                ...queryParams
            };

            // REALIZAR LA PETICIÓN AL BACKEND
            const result = await this.api.get<PaginatedBudgetsResponse | BudgetDto[]>({
                url: EndPoints.getBudgets,
                queryParams: params
            });

            if (!result) {
                return null;
            }

            console.log('📦 Resultados del API:', result);

            // VERIFICAR SI ES UN ARRAY DIRECTAMENTE O UN OBJETO PAGINADO
            let budgetsArray: any[];
            let totalRecords: number;

            if (Array.isArray(result)) {
                // El API devolvió un array directamente
                budgetsArray = result;
                totalRecords = result.length;
                console.log('📦 API devolvió array directamente. Total:', totalRecords);
            } else {
                // El API devolvió un objeto paginado
                budgetsArray = result.results || [];
                totalRecords = result.count || 0;
                console.log('📦 API devolvió objeto paginado. Total:', totalRecords);
            }

            // MAPEAR LOS RESULTADOS A BudgetDto
            const budgetsList = budgetsArray.map((item: any) => BudgetDto.fromJson(item));
            console.log('📦 Budgets mapeados:', budgetsList.length);

            return {
                list: budgetsList,
                totalRecords: totalRecords
            };
        } catch (error) {
            console.error('Error al obtener presupuestos:', error);
            return null;
        }
    }
    async getBudget(budgetId: number): Promise<BudgetDto | null> {
        try {
            const url = EndPoints.getBudget.replace('{budgetId}', budgetId.toString());

            const result = await this.api.get<BudgetDto>({
                url,
                
            });

            if (!result) {
                return null;
            }

            return BudgetDto.fromJson(result);
        } catch (error) {
            console.error('Error al actualizar presupuesto:', error);
            return null;
        }
    }


    /**
     * OBTENER TOP PRESUPUESTOS POR TRANSPORTE
     * @param transportId - ID del transporte
     * @returns Lista de los mejores presupuestos
     */
    async getTopBudgets(transportId: number): Promise<{ list: BudgetDto[], totalRecords: number } | null> {
        try {
            const url = EndPoints.getTopBudgets.replace('{transportId}', transportId.toString());

            const result = await this.api.get<BudgetDto[]>({
                url
            });

            if (!result) {
                return null;
            }

            console.log('📦 Top Budgets del API:', result);

            const budgetsArray = Array.isArray(result) ? result : [];
            const budgetsList = budgetsArray.map((item: any) => BudgetDto.fromJson(item));

            return {
                list: budgetsList,
                totalRecords: budgetsList.length
            };
        } catch (error) {
            console.error('Error al obtener top presupuestos:', error);
            return null;
        }
    }

    async acceptBudget(budgetId: number, message?: string): Promise<BudgetOperationResult<BudgetDto>> {
        try {
            const url = EndPoints.acceptBudget.replace('{budgetId}', budgetId.toString());

            // Usar FormData como indica el curl del API
            const formData = new FormData();
            if (message) {
                formData.append('message', message);
            }

            const result = await this.api.post<BudgetDto>({
                url,
                body: formData
            });

            if (!result) {
                return { success: false, errorMessage: 'Error al aceptar el presupuesto' };
            }

            return { success: true, data: BudgetDto.fromJson(result) };
        } catch (error) {
            console.error('Error al aceptar presupuesto:', error);
            const errorMessage = this.extractErrorMessage(error);
            return { success: false, errorMessage };
        }
    }

    async rejectBudgets(budgetIds: number[], rejectionReason?: string): Promise<BudgetOperationResult<boolean>> {
        try {
            // Usar FormData como indica el curl del API
            const formData = new FormData();
            formData.append('budget_ids', budgetIds.join(','));
            if (rejectionReason) {
                formData.append('rejection_reason', rejectionReason);
            }

            const result = await this.api.post<any>({
                url: EndPoints.rejectBudgets,
                body: formData
            });

            if (result === null) {
                return { success: false, errorMessage: 'Error al rechazar los presupuestos' };
            }

            return { success: true, data: true };
        } catch (error) {
            console.error('Error al rechazar presupuestos:', error);
            const errorMessage = this.extractErrorMessage(error);
            return { success: false, errorMessage };
        }
    }

    async createBudgets(budgets: CreateBudgetRequest[]): Promise<BudgetOperationResult<BudgetDto[]>> {
        try {
            const result = await this.api.post<BudgetDto[]>({
                url: EndPoints.createBudget,
                body: budgets
            });

            if (!result) {
                return { success: false, errorMessage: 'Error al crear el presupuesto' };
            }

            const list = Array.isArray(result)
                ? result.map((item: any) => BudgetDto.fromJson(item))
                : [BudgetDto.fromJson(result)];

            return { success: true, data: list };
        } catch (error) {
            console.error('Error al crear presupuesto:', error);
            const errorMessage = this.extractErrorMessage(error);
            return { success: false, errorMessage };
        }
    }

    /**
     * CALCULAR MARGEN DE PRESUPUESTO EN BACKEND
     * @param data - Datos de transporte, conductor y costes
     * @returns Margen en EUR y porcentaje calculado por backend
     */
    async calculateMargin(data: CalculateMarginRequestDto): Promise<CalculateMarginResponseDto | null> {
        try {
            const result = await this.api.post<CalculateMarginResponseDto>({
                url: EndPoints.calculateBudgetMargin,
                body: data
            });

            if (!result) {
                return null;
            }

            return CalculateMarginResponseDto.fromJson(result);
        } catch (error) {
            console.error('Error al calcular el margen del presupuesto:', error);
            return null;
        }
    }

    async updateBudget(budgetId: number, updateData: Partial<UpdateBudgetRequest>): Promise<BudgetDto | null> {
        try {
            const url = EndPoints.updateBudget.replace('{budgetId}', budgetId.toString());

            const result = await this.api.patch<BudgetDto>({
                url,
                body: updateData
            });

            if (!result) {
                return null;
            }

            return BudgetDto.fromJson(result);
        } catch (error) {
            console.error('Error al actualizar presupuesto:', error);
            return null;
        }
    }

    /**
     * Extrae el mensaje de error de una respuesta HTTP
     */
    private extractErrorMessage(error: unknown): string {
        if (error instanceof HttpErrorResponse) {
            // Intentar obtener el mensaje del cuerpo de la respuesta
            if (error.error?.message) {
                return error.error.message;
            }
            if (error.error?.detail) {
                return error.error.detail;
            }
            if (typeof error.error === 'string') {
                return error.error;
            }
            // Fallback al mensaje genérico del error HTTP
            return error.message || 'Error desconocido';
        }
        if (error instanceof Error) {
            return error.message;
        }
        return 'Error desconocido';
    }
}

export interface AcceptBudgetRequest {
    total_price: string;
    departure_datetime: string;
    arrival_datetime: string;
    base_price: string;
    fuel_price: string;
    transport_cost_outbound: string;
    transport_cost_return: string;
    optional_expenses_price: string;
    accommodation_cost: string;
    toll_expense: string;
    per_diem_cost: string;
    budget_status: string;
    invalidation_reason: string;
    driver: number;
    leg: number;
}

export interface RejectBudgetsRequest {
    budget_ids: number[];
}

export interface CreateBudgetRequest {
    transport_id: number;
    departure_datetime: string;
    arrival_datetime: string;
    base_price: number;
    transport_cost_outbound: number;
    transport_cost_return: number;
    toll_expense: number;
    ferry_cost: number;
    exceptional_expense: number;
    fuel_price: number;
    electric_charge_cost: number;
    driver_id: number;
}

export interface UpdateBudgetRequest {
    departure_datetime?: string;
    arrival_datetime?: string;
    base_price?: string;
    transport_cost_outbound?: string;
    transport_cost_return?: string;
    toll_expense?: string;
    ferry_cost?: string;
    exceptional_expense?: string;
    fuel_price?: string;
    electric_charge_cost?: string;
    driver_id?: number;
}

